import React from 'react';
import { Layers, Printer, HelpCircle, FileSpreadsheet, BadgeIndianRupee, Settings, Send, Keyboard, LayoutDashboard } from 'lucide-react';
import { TagBrand, TagCompanyPill, TagUserMenu, TagLogo } from './TagChrome';
import { tagThemeClass } from './tagTheme';
import { TagFontSize } from './TagFontSize';

// --- Concept A: Context Bar (sticky topbar + company pill) -----------------
function HeaderA({ headerExtra, principal, companyName, isTagAdmin, onLogout }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70" data-testid="tagws-header-a">
      <div className="mx-auto flex min-h-14 max-w-7xl flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 py-2 sm:h-14 sm:flex-nowrap sm:py-0 sm:px-6 lg:px-8">
        <TagBrand />
        <TagCompanyPill companyName={companyName} />
        {headerExtra ? <div className="hidden md:flex">{headerExtra}</div> : null}
        <div className="flex items-center gap-1">
          <TagFontSize principal={principal} />
          <TagUserMenu principal={principal} companyName={companyName} isTagAdmin={isTagAdmin} onLogout={onLogout} />
        </div>
      </div>
    </header>
  );
}

// --- Concept B: Split Header (brand row + utility/toolbar row) --------------
function HeaderB({ headerExtra, principal, companyName, isTagAdmin, onLogout, toolbar }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur" data-testid="tagws-header-b">
      <div className="mx-auto flex h-12 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <TagBrand />
          <TagCompanyPill companyName={companyName} />
          {headerExtra}
        </div>
        <div className="flex items-center gap-1">
          <TagFontSize principal={principal} />
          <TagUserMenu principal={principal} companyName={companyName} isTagAdmin={isTagAdmin} onLogout={onLogout} />
        </div>
      </div>
      <div className="border-t border-border bg-background/60">
        <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">{toolbar}</div>
      </div>
    </header>
  );
}

// --- Concept C: Rail + Topbar (icon rail for print workflow) ----------------
function HeaderC({ headerExtra, principal, companyName, isTagAdmin, onLogout, rail = {} }) {
  const railItems = [
    { icon: Layers, label: 'Jewellery tags (Alt + Q)', id: 'jewellery', active: rail.tagsActive !== false, onClick: rail.onTags || (() => window.scrollTo({ top: 0, behavior: 'smooth' })) },
    ...(rail.onDashboard ? [{ icon: LayoutDashboard, label: rail.dashLabel || 'Dashboard (Alt + G, H)', id: 'dashboard', active: !!rail.dashActive, onClick: rail.onDashboard }] : []),
    { icon: Printer, label: 'Batch print', id: 'print', onClick: rail.onPrint },
    ...(rail.onImport ? [{ icon: FileSpreadsheet, label: 'Import from Excel', id: 'import', onClick: rail.onImport }] : []),
    ...(rail.onSales ? [{ icon: BadgeIndianRupee, label: 'Sales', id: 'sales', onClick: rail.onSales }] : []),
    ...(rail.onApprovals ? [{ icon: Send, label: 'Approval register', id: 'approvals', onClick: rail.onApprovals }] : []),
    ...(rail.onShortcuts ? [{ icon: Keyboard, label: 'Keyboard shortcuts', id: 'shortcuts', onClick: rail.onShortcuts }] : []),
    ...(rail.onSettings ? [{ icon: Settings, label: 'Settings', id: 'settings', onClick: rail.onSettings }] : []),
    { icon: HelpCircle, label: 'Quick guide', id: 'help', onClick: rail.onHelp },
  ];
  return (
    <>
      <aside className="fixed left-0 top-0 z-40 hidden h-full w-16 flex-col items-center gap-1 border-r border-border bg-card py-3 md:flex" data-testid="tagws-nav-rail">
        <a href="/" className="mb-3" title="Home" data-testid="tagws-nav-rail-logo"><TagLogo size={42} /></a>
        {railItems.map((it) => {
          const I = it.icon;
          return (
            <button
              key={it.id} title={it.label} aria-label={it.label} data-testid={`tagws-nav-rail-${it.id}`} onClick={it.onClick}
              className={`grid h-10 w-10 place-items-center rounded-lg transition-colors duration-150 ${it.active ? 'bg-secondary text-foreground ring-1 ring-[hsl(var(--ring))]/40' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}
            >
              <I size={18} />
            </button>
          );
        })}
      </aside>
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur md:pl-16" data-testid="tagws-header-c">
        <div className="mx-auto flex min-h-14 max-w-7xl flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 sm:h-14 sm:flex-nowrap sm:py-0 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3 md:hidden"><TagBrand /></div>
          <TagCompanyPill companyName={companyName} />
          {headerExtra ? <div className="hidden min-w-0 flex-1 justify-center md:flex">{headerExtra}</div> : null}
          <div className="ml-auto flex items-center gap-1">
            <TagFontSize principal={principal} />
          <TagUserMenu principal={principal} companyName={companyName} isTagAdmin={isTagAdmin} onLogout={onLogout} />
          </div>
        </div>
      </header>
    </>
  );
}

/**
 * TagWorkspaceShell — the neutral white-label chrome for metriqs.co.in.
 * Wraps everything in `.tag-scope` + the chosen colour theme so the tokens
 * apply and never leak into the Inventique portal or Notes module. The header
 * concept is swappable (a | b | c).
 */
export function TagWorkspaceShell({
  variant = 'a', principal, companyName, isTagAdmin, onLogout, toolbar, rail, headerExtra, children, wide = false,
}) {
  const v = (variant || 'a').toLowerCase();
  const chrome = { principal, companyName, isTagAdmin, onLogout, headerExtra };
  return (
    <div className={`tag-scope ${tagThemeClass()} min-h-screen bg-background text-foreground`} data-testid="tagws-shell">
      {v === 'b' ? <HeaderB {...chrome} toolbar={toolbar} />
        : v === 'c' ? <HeaderC {...chrome} rail={rail} />
          : <HeaderA {...chrome} />}

      <div className={v === 'c' ? 'md:pl-16' : ''}>
        {v !== 'b' && toolbar ? (
          <div className="sticky top-14 z-20 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
            <div className="mx-auto max-w-7xl px-3 py-2.5 sm:px-6 lg:px-8">{toolbar}</div>
          </div>
        ) : null}
        <main className={`mx-auto px-3 pb-28 pt-4 sm:px-6 md:py-6 lg:px-8 ${wide ? 'max-w-none' : 'max-w-7xl'}`}>{children}</main>
      </div>
    </div>
  );
}
